import * as React from "react"
import {
  ObjectKind,
  type WorkItem,
  WorkItemPushFieldType,
  WorkItemReviewState,
  type WorkItemTransaction,
} from "@digits-graphql/frontend/graphql-bearer"
import { useOnBodyClick } from "@digits-shared/hooks/useOnBodyClick"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { useUpdateParty } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Actions/Shared"
import { useAcceptField } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/hooks/useAcceptField"
import { useWorkItemPush } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/hooks/useWorkItemPush"
import { FieldStatus } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/reducers/workItemReducer"
import {
  ClosedToValue,
  DropdownIndicator,
  FIELD_PUSHED,
  Header,
  HIGHLIGHT_FIELD,
  StyledDetailColumn,
  StyledPlaceholderText,
  StyledToContainer,
  ToValue,
} from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Shared/TransactionReviewWorkItemRowSharedElements"
import {
  DETAIL_COLUMN_ACTIVE_CLASSNAME,
  DetailColumn,
  DetailContainer,
  ToValueText,
  type WorkItemRowProps,
} from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Shared/WorkItemRowSharedElements"
import { InputActions } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/WorkItemDetails/InputActions"
import { WorkItemErrorPopover } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/WorkItemDetails/WorkItemErrorPopOver"
import { useWorkItemRowContext } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/WorkItemRowContext"
import { useWorkItemsContext } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemsContext"
import { useSupportingParties } from "src/frontend/components/OS/Shared/WorkItems/useSupportingParties"
import { useSupportingTransactions } from "src/frontend/components/OS/Shared/WorkItems/useSupportingTransactions"
import { FrontendPartyRole } from "src/frontend/types/FrontendPartyRole"
import { type PartyWithNewEntitiesTypeaheadResult } from "src/shared/components/Typeahead/sharedTypeahead"
import { Typeahead } from "src/shared/components/Typeahead/Typeahead"

const DigitsSuggestsText = styled.div`
  display: inline-flex;
  text-transform: uppercase;
  color: ${colors.primary};
  padding-left: 4px;
  font-size: 9px;
  align-items: center;
`

/*
  COMPONENTS
*/

export const VendorDetail: React.FC<WorkItemRowProps> = ({ workItem }) => {
  const { isClosed } = useWorkItemsContext()

  // if item is closed, and we don't have a suggestion, just show the current party
  if (isClosed) {
    return <ClosedPartyDetails workItem={workItem} />
  }

  return <OpenPartyDetails workItem={workItem} />
}

const OpenPartyDetails: React.FC<WorkItemRowProps> = ({ workItem }) => {
  const { object: transaction } = useSupportingTransactions(workItem)
  const partyRole = usePartyRole(transaction)
  const {
    isActive,
    showActions,
    isUserProvided,
    partyName,
    updateParty,
    hasError,
    classes,
    animateHeader,
    hasSuggestion,
  } = usePartyState(workItem)

  const { hideRetryButton, onAcceptClick, onIgnoreClick, disabled } = useAcceptField(
    workItem,
    WorkItemPushFieldType.Party
  )

  const {
    value: showTypeahead,
    setTrue: setShowTypeahead,
    setFalse: setHideTypeahead,
  } = useStateBoolean()

  const clickShowTypeahead = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      setShowTypeahead()
    },
    [setShowTypeahead]
  )

  return (
    <StyledDetailColumn animateHeader={animateHeader}>
      <StyledToContainer
        isUserProvided={isUserProvided || !updateParty}
        hasSuggestion={hasSuggestion}
        hasError={hasError}
        className={showTypeahead || isActive ? DETAIL_COLUMN_ACTIVE_CLASSNAME : ""}
      >
        <Header>
          {partyRole}
          {hasSuggestion && <DigitsSuggestsText>Digits Suggests</DigitsSuggestsText>}
        </Header>
        <ToValue
          className={[
            showTypeahead || isActive ? DETAIL_COLUMN_ACTIVE_CLASSNAME : "",
            classes,
          ].join(" ")}
          onClick={clickShowTypeahead}
        >
          {partyName ? (
            <ToValueText>{partyName}</ToValueText>
          ) : (
            <StyledPlaceholderText>{`Add ${partyRole}`}</StyledPlaceholderText>
          )}
          {!hasSuggestion && <DropdownIndicator />}

          <InputActions
            showActions={showActions}
            animationKey={`${workItem.workItemId}-party`}
            hasSuggestion={hasSuggestion}
            hasError={hasError}
            disabled={disabled}
            onAccept={onAcceptClick}
            onIgnore={onIgnoreClick}
            onEdit={clickShowTypeahead}
            hideRetry={hideRetryButton}
            ledgerType={workItem.ledgerType}
          />
        </ToValue>

        {hasError && (
          <WorkItemErrorPopover workItem={workItem} field={WorkItemPushFieldType.Party} />
        )}

        {showTypeahead && (
          <EditVendorSuggestion workItem={workItem} setHideTypeahead={setHideTypeahead} />
        )}
      </StyledToContainer>
    </StyledDetailColumn>
  )
}

const ClosedPartyDetails: React.FC<WorkItemRowProps> = ({ workItem }) => {
  // favors userProvided party then the to party if the client has not responded
  const updateParty = useUpdateParty(workItem)

  // object transaction
  const { object: transaction } = useSupportingTransactions(workItem)

  // partyRole will default to Vendor if the displayPartyRole is undefined or not found
  const partyRole = usePartyRole(transaction)

  return (
    <DetailColumn animateHeader>
      <DetailContainer>
        <Header>{partyRole}</Header>
        <ClosedToValue>
          <ToValueText>{updateParty?.name || transaction?.party?.name || partyRole}</ToValueText>
        </ClosedToValue>
      </DetailContainer>
    </DetailColumn>
  )
}

const EditVendorSuggestion: React.FC<{
  workItem: WorkItem
  setHideTypeahead: () => void
}> = ({ workItem, setHideTypeahead }) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const { dispatch } = useWorkItemRowContext()
  const updateParty = useUpdateParty(workItem)
  const { object: transaction, userProvided } = useSupportingTransactions(workItem)

  const onResultSelected = React.useCallback(
    (data: PartyWithNewEntitiesTypeaheadResult) => {
      const { entity } = data
      if (!workItem || !entity) return

      dispatch({
        type: "UpdateField",
        entity,
        previousEntity: updateParty || userProvided?.party || transaction?.party,
        field: WorkItemPushFieldType.Party,
      })

      setHideTypeahead()
    },
    [dispatch, setHideTypeahead, transaction?.party, updateParty, userProvided?.party, workItem]
  )

  useOnBodyClick(inputRef, setHideTypeahead)

  return (
    <Typeahead
      ref={inputRef}
      kind={ObjectKind.Party}
      onResultSelected={onResultSelected}
      allowAddNew
      close={setHideTypeahead}
    />
  )
}

function usePartyRole(transaction?: WorkItemTransaction) {
  return React.useMemo(() => {
    if (transaction?.displayPartyRole) {
      const frontendRole = FrontendPartyRole.findByRole(transaction.displayPartyRole)
      if (frontendRole !== FrontendPartyRole.Unknown) {
        return frontendRole.displayName
      }
    }

    return "Vendor"
  }, [transaction?.displayPartyRole])
}

export function usePartyState(workItem: WorkItem) {
  const updateParty = useUpdateParty(workItem)
  const { state } = useWorkItemRowContext()
  const partyStatus = state.fieldStatuses?.get(WorkItemPushFieldType.Party)
  const { hasError } = useWorkItemPush(workItem, WorkItemPushFieldType.Party)
  const { object: transaction, userProvided, to } = useSupportingParties(workItem)

  return React.useMemo(() => {
    const isUpdated = partyStatus === FieldStatus.Updated
    const isPushed = partyStatus === FieldStatus.Pushed
    const hasClientResponded = workItem.reviewState === WorkItemReviewState.ClientResponded
    const hasSuggestion = !userProvided && !!to && !hasClientResponded && !isPushed

    const classes = []
    if (isPushed) classes.push(FIELD_PUSHED)
    if ((isUpdated || hasSuggestion) && !isPushed && state.highlightUpdated)
      classes.push(HIGHLIGHT_FIELD)

    return {
      hasSuggestion,
      hasError,
      isActive: !!updateParty && isUpdated,
      showActions: (isUpdated || hasSuggestion) && !isPushed,
      isUserProvided: !!userProvided || isPushed,
      partyName: updateParty?.name || userProvided?.name || transaction?.name,
      updateParty,
      classes: classes.join(" "),
      animateHeader: !hasError && !isUpdated && !hasSuggestion,
    }
  }, [
    hasError,
    partyStatus,
    state.highlightUpdated,
    to,
    transaction?.name,
    updateParty,
    userProvided,
    workItem.reviewState,
  ])
}
